import * as THREE from "three";

/**
 * Simple extended THREE.Mesh that loads a textured
 * with the loadTexture method
 */
export default class TexturedGeoMesh extends THREE.Mesh {
  material;
  materials;
  fullyLoadedMaterials;
  realBoundingBox;
  cameraDirection;
  cameraPosition;
  player;
  flipTexture;
  worldRotationDeg;

  /**
   * Creates mesh using given geometry
   *
   * @param {Player} player
   * @param {THREE.Geometry|THREE.BufferGeometry} geometry
   * @param {THREE.MeshBasicMaterial[]|string} materialType
   * @param onTextureSet
   * @param worldRotationDeg
   */
  constructor(
    player,
    geometry,
    materialType = "basic",
    onTextureSet = () => {},
    worldRotationDeg = 90,
  ) {
    let _materials = null;
    let faceMesh;
    let loader = new THREE.TextureLoader();

    let faces = ["left", "right", "above", "below", "back", "front"];

    switch (materialType) {
      case "phong":
        faceMesh = new THREE.MeshPhongMaterial();
        break;
      case "cube":
        _materials = [];
        let colors = ["red", "blue", "white", "yellow", "green", "magenta"];
        let dbg = window.location.search.indexOf("cube_dbg=true") > -1;
        if (dbg) {
          console.log(faces);
          console.log(colors);
        }
        for (let x = 0; x < 6; x++) {
          let face = new THREE.MeshBasicMaterial({
            color: dbg ? colors[x] : "white", // 'white',
            side: THREE.DoubleSide,
            map: loader.load(),
            name: faces[x],
          });
          face.map.anisotropy =
            "renderer" in player
              ? player.renderer.capabilities.getMaxAnisotropy()
              : null;
          face.map.minFilter = face.map.magFilter = THREE.LinearFilter;

          _materials.push(face);
        }
        break;
      default:
        if (typeof materialType === "string") {
          faceMesh = new THREE.MeshBasicMaterial();
        } else {
          _materials = materialType;
        }
        break;
    }

    if (faceMesh) {
      faceMesh.map = loader.load();
      faceMesh.map.anisotropy =
        "renderer" in player
          ? player.renderer.capabilities.getMaxAnisotropy()
          : null;
      faceMesh.map.minFilter = faceMesh.map.magFilter = THREE.LinearFilter;
    }

    super(geometry, _materials ?? faceMesh);

    if (worldRotationDeg !== undefined) {
      this.worldRotationDeg = worldRotationDeg;
      this.rotateY(worldRotationDeg * (Math.PI / 180));
    }

    this.player = player;

    if (_materials) {
      for (let x = 0; x < 6; x++) {
        this.disposeMaterial(_materials[x]);
      }
      this.material = _materials;
      if ("cube" === materialType) {
        this.materials = [];
        this.fullyLoadedMaterials = [];
        // map materials to faces
        for (let x in _materials) {
          this.materials[faces[x]] = _materials[x];
          this.fullyLoadedMaterials[faces[x]] = false;
        }
      } else {
        this.materials = _materials;
        this.fullyLoadedMaterials = [false, false, false, false, false, false];
      }
    } else if (faceMesh) {
      faceMesh.dispose();
      this.material = faceMesh;
      this.fullyLoadedMaterials = false;
    }

    geometry.dispose();

    this.onTextureSet = onTextureSet;
    this.flipTexture = false;

    this.realBoundingBox = new THREE.Box3();
    this.realBoundingBox.setFromObject(this);

    this.cameraPosition = new THREE.Vector3();
    this.cameraDirection = new THREE.Vector3();
    this.updateCameraCoords();

    this.addListeners();
  }

  /**
   * @returns {null|*}
   */
  updateCameraCoords() {
    let camera = this.player.camera;
    if (camera) {
      camera.updateMatrix();
      camera.updateMatrixWorld();
      camera.updateProjectionMatrix();

      camera.getWorldPosition(this.cameraPosition);
      camera.getWorldDirection(this.cameraDirection);

      return camera;
    }

    return null;
  }

  /**
   * @param texture
   * @param full
   */
  setTexture(texture, full = false) {
    if (
      texture &&
      "material" in this &&
      this.material !== undefined &&
      "map" in this.material
    ) {
      if (this.fullyLoadedMaterials !== true || full === true) {
        this.disposeMaterial(this.material);
        this.material.map = texture;

        if (this.onTextureSet) {
          this.onTextureSet(texture, full);
        }
      }

      if (full) {
        this.fullyLoadedMaterials = true;
      }

      this.realBoundingBox.setFromObject(this);
    } else {
      if (this.material === undefined) {
        console.warn("Could not set texture, material is undefined");
      } else {
        throw Error("Could not set texture");
      }
    }
  }

  /**
   *
   * @param image
   * @param flip
   * @param callback
   * @param stereo
   * @param lowRes
   * @param resolution
   * @returns {Promise}
   */
  async getTexture(
    image,
    flip,
    callback = () => {},
    stereo,
    lowRes,
    resolution,
  ) {
    return await new Promise((resolve, reject) => {
      this.player.textureManager.fetch({
        image,
        flip,
        callback: ({ texture, full, res }) => {
          callback(texture, full, res);
          resolve(texture);
        },
        nonCritical: false,
        stereo,
        lowRes,
        resolution,
        isThumbnail: this.player.isThumbnail,
      });
    });
  }

  /**
   * Loads texture onto mesh material from url
   *
   * @param {object} image
   * @param {boolean} flip - whether to flip the texture
   * @param callback
   * @param stereo
   * @param lowRes
   */
  loadTexture(image, flip, callback = () => {}, stereo, lowRes) {
    return new Promise((resolve, reject) => {
      try {
        this.getTexture(
          image,
          flip,
          (texture, full, res) => {
            this.setTexture(texture, full);
            if (full) {
              this.fullyLoadedMaterials = true;
            }
            callback(full, res);
            resolve(texture);
          },
          stereo,
          lowRes,
        );
      } catch (e) {
        callback(false, null);
        reject();
      }
    });
  }

  /**
   *
   * @param lowRes
   * @param callback
   * @returns {Promise}
   */
  async loadImage(lowRes, callback) {
    await this.loadTexture(
      this.image,
      this.flipTexture,
      callback,
      this.stereo,
      lowRes,
    );
  }

  /**
   *
   * @param resolution
   * @returns {Promise}
   */
  async getCurrentTexture(resolution) {
    return await this.getTexture(
      this.image,
      this.flipTexture,
      () => {},
      this.stereo,
      undefined,
      resolution,
    );
  }

  /**
   *
   * @returns {{aspect: number, width: number, radius: number, height: number}}
   */
  getRealDimensions() {
    let width = this.realBoundingBox.max.x - this.realBoundingBox.min.x;
    let height = this.realBoundingBox.max.y - this.realBoundingBox.min.y;

    return {
      radius: width / 2,
      height: height,
      width: width,
      aspect: (Math.PI * width) / height,
    };
  }

  /**
   *
   * @returns {{aspect: number, width: number, radius: number, height: number}}
   */
  getGeometryDimensions() {
    if (!this.geometry.boundingBox) {
      this.geometry.computeBoundingBox();
    }

    let width =
      this.geometry.boundingBox.max.x - this.geometry.boundingBox.min.x;
    let height =
      this.geometry.boundingBox.max.y - this.geometry.boundingBox.min.y;

    return {
      radius: width / 2,
      height: height,
      width: width,
      aspect: (Math.PI * width) / height,
    };
  }

  getHotspotOptions(is3D = false) {
    if (this.imageFov) {
      return {
        distance: 500,
        scale: (this.imageFov / 80) * (is3D ? 0.7 : 1),
      };
    }

    return {
      distance: 500,
      scale: is3D ? 0.7 : 1,
    };
  }

  /**
   *
   * @returns {Mesh}
   */
  projectSphere(copyRotation = false) {
    let geometry = new THREE.SphereBufferGeometry(600, 100, 100);
    let material = new THREE.MeshBasicMaterial({
      transparent: true,
      side: THREE.DoubleSide,
    });
    let mesh = new THREE.Mesh(geometry, material);
    if (copyRotation) {
      mesh.rotation.copy(this.rotation);
    }
    mesh.rotateY(this.worldRotationDeg * (Math.PI / 180));

    return mesh;
  }

  afterTransition() {}

  addListeners() {}

  removeListeners() {}

  disposeMaterial(material) {
    if (material.map) {
      material.map.dispose();
    }
    if (material.bumpMap) {
      material.bumpMap.dispose();
    }
    if (material.normalMap) {
      material.normalMap.dispose();
    }
    if (material.specularMap) {
      material.specularMap.dispose();
    }
    material.dispose();
  }

  dispose() {
    this.removeListeners();
    this.geometry.dispose();
    this.geometry = undefined;
    if (Array.isArray(this.material)) {
      for (let x in this.material) {
        this.disposeMaterial(this.material[x]);
        this.material[x] = undefined;
      }
    } else {
      this.disposeMaterial(this.material);
      this.material = undefined;
    }
    // this.player = undefined;
    // this.cameraDirection = undefined;
    // this.cameraPosition = undefined;
    // this.realBoundingBox = undefined;
  }
}
